import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  GripVertical,
  MoreHorizontal,
  Calendar,
  User,
  MessageSquare,
  Building2,
  Eye,
  Edit,
  Trash2,
  AlertCircle,
  UserPlus,
  Users,
} from 'lucide-react'
import { TareaPreviewContent } from '@/components/tareas/TareaPreviewContent'
import { EditableDateCell } from './EditableDateCell'
import { InscripcionBadge } from './Inscripcion'
import type { TareaCompleta } from '@/types/database'
import type { GrupoDeTablero } from '@/core/services/tareasGrupoService'
import { useUserNameResolver } from '@/core/hooks/useUserNameResolver'
import { usePermissions } from '@/hooks/usePermissions'
import { format, parseISO, isToday, differenceInCalendarDays, startOfDay } from 'date-fns'
import { es } from 'date-fns/locale'
import { cn } from '@/lib/utils'

interface TareaCardProps {
  tarea: TareaCompleta
  onVerDetalle?: (tarea: TareaCompleta) => void
  onEditar?: (tarea: TareaCompleta) => void
  onEliminar?: (tarea: TareaCompleta) => void
  onAsignarAMi?: (tarea: TareaCompleta) => void
  currentUserEmail?: string
  isDragging?: boolean
  // Permisos
  canEdit?: boolean
  canDelete?: boolean
  // Props para selección múltiple
  selectable?: boolean
  selected?: boolean
  onSelectionChange?: (selected: boolean) => void
}

const ESTADO_STYLES: Record<string, string> = {
  pendiente: 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900 dark:text-yellow-300 dark:border-yellow-800',
  en_proceso: 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900 dark:text-blue-300 dark:border-blue-800',
  completada: 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900 dark:text-green-300 dark:border-green-800',
}

const ESTADO_LABELS: Record<string, string> = {
  pendiente: 'Pendiente',
  en_proceso: 'En Proceso',
  completada: 'Completada',
}

export function TareaCard({
  tarea,
  onVerDetalle,
  onEditar,
  onEliminar,
  onAsignarAMi,
  currentUserEmail,
  isDragging = false,
  canEdit = true,
  canDelete = true,
  selectable = false,
  selected = false,
  onSelectionChange,
}: TareaCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({ id: tarea.id, disabled: selectable })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const resolveNombre = useUserNameResolver()
  const fechaLimite = tarea.fecha_limite ? parseISO(tarea.fecha_limite) : null
  const diasRestantes = fechaLimite ? differenceInCalendarDays(fechaLimite, startOfDay(new Date())) : null
  const estaVencida = diasRestantes !== null && diasRestantes < 0 && tarea.estado !== 'completada'
  const esHoy = fechaLimite && isToday(fechaLimite)

  // Handler de click a nivel de Card
  const handleCardClick = (e: React.MouseEvent) => {
    // No hacer nada si estamos arrastrando
    if (isDragging || isSortableDragging) return
    // No hacer nada en modo selección (el checkbox maneja eso)
    if (selectable) return
    // Verificar que el click no fue en un elemento interactivo
    const target = e.target as HTMLElement
    if (target.closest('button') || target.closest('[role="menuitem"]')) return
    // Abrir detalle
    onVerDetalle?.(tarea)
  }

  return (
    <Card
      ref={setNodeRef}
      style={style}
      onClick={handleCardClick}
      className={cn(
        'cursor-pointer transition-all hover:shadow-md',
        (isDragging || isSortableDragging) && 'opacity-50 shadow-lg rotate-2',
        estaVencida && 'border-red-300 bg-red-50/50 dark:border-red-700 dark:bg-red-950/50',
        esHoy && !estaVencida && 'border-orange-300 bg-orange-50/50 dark:border-orange-700 dark:bg-orange-950/50',
        // Modo selección
        selected && 'bg-accent/10 border-accent'
      )}
    >
      <CardContent className="p-3">
        <div className="flex items-start gap-2">
          {/* Checkbox de selección o handle de drag */}
          {selectable ? (
            <Checkbox
              checked={selected}
              onCheckedChange={(checked) => onSelectionChange?.(checked === true)}
              className="mt-1 shrink-0"
              aria-label={`Seleccionar ${tarea.titulo}`}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <button
              className="mt-1 cursor-grab text-muted-foreground hover:text-foreground"
              {...attributes}
              {...listeners}
            >
              <GripVertical className="h-4 w-4" />
            </button>
          )}

          {/* Contenido con HoverCard para preview */}
          <HoverCard openDelay={250} closeDelay={100}>
            <HoverCardTrigger asChild>
              <div className="flex-1 min-w-0">
                {/* Estado y menú */}
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="flex flex-wrap items-center gap-1">
                    {tarea.prioridad === 'urgente' && (
                      <Badge variant="outline" className="text-xs bg-red-100 text-red-800 border-red-200 dark:bg-red-900 dark:text-red-300 dark:border-red-800">
                        Urgente
                      </Badge>
                    )}
                    {tarea.tipo === 'inscripcion' && <InscripcionBadge />}
                    <Badge
                      variant="outline"
                      className={cn('text-xs', ESTADO_STYLES[tarea.estado as string])}
                    >
                      {ESTADO_LABELS[tarea.estado as string] ?? tarea.estado}
                    </Badge>
                  </div>

                  <div onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-6 w-6">
                          <MoreHorizontal className="h-3 w-3" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onVerDetalle?.(tarea)}>
                          <Eye className="mr-2 h-4 w-4" />
                          Ver detalle
                        </DropdownMenuItem>
                        {canEdit && (
                          <DropdownMenuItem onClick={() => onEditar?.(tarea)}>
                            <Edit className="mr-2 h-4 w-4" />
                            Editar
                          </DropdownMenuItem>
                        )}
                        {onAsignarAMi && tarea.asignado_a !== currentUserEmail && (
                          <DropdownMenuItem onClick={() => onAsignarAMi(tarea)}>
                            <UserPlus className="mr-2 h-4 w-4" />
                            Asignar a mí
                          </DropdownMenuItem>
                        )}
                        {canDelete && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => onEliminar?.(tarea)}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Eliminar
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>

                {/* Título */}
                <h4 className="font-medium text-sm line-clamp-2 mb-2">
                  {tarea.titulo}
                </h4>

                {/* Metadata */}
                <div className="space-y-1">
                  {/* Cliente */}
                  {tarea.cliente_nombre && tarea.cliente_nombre.replace(/,/g, '').trim() && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Building2 className="h-3 w-3" />
                      <span className="truncate">{tarea.cliente_nombre}</span>
                    </div>
                  )}

                  {/* Vencimiento fiscal relacionado */}
                  {tarea.tipo_impuesto && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <AlertCircle className="h-3 w-3" />
                      <span className="truncate">
                        {tarea.tipo_impuesto} - {tarea.vencimiento_periodo}
                      </span>
                    </div>
                  )}

                  {/* Asignado */}
                  {tarea.asignado_a && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <User className="h-3 w-3" />
                      <span className="truncate">
                        {resolveNombre(tarea.asignado_a)}
                      </span>
                    </div>
                  )}

                  {/* Fecha límite — editable con un click, igual que en el listado (ORR-330).
                      Sin fecha se dibuja igual mientras se pueda editar: si no, desde el kanban
                      no habría manera de ponérsela. */}
                  {(fechaLimite || canEdit) && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Calendar className="h-3 w-3" />
                      <EditableDateCell
                        tareaId={tarea.id}
                        fechaLimite={tarea.fecha_limite}
                        estado={tarea.estado as string}
                        canEdit={canEdit}
                        emptyLabel="Sin fecha"
                        inputClassName="h-6 text-xs"
                        formatLabel={(fecha) =>
                          estaVencida
                            ? `Vencida hace ${Math.abs(diasRestantes!)}d`
                            : esHoy
                              ? 'Vence hoy'
                              : format(fecha, "d 'de' MMM", { locale: es })
                        }
                      />
                    </div>
                  )}

                  {/* Asignado por (si fue delegada) */}
                  {(() => {
                    const asignadoPor = (tarea as unknown as { asignado_por?: string | null }).asignado_por
                    if (asignadoPor && asignadoPor !== tarea.asignado_a) {
                      return (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground/70">
                          <span>↳ delegada por {resolveNombre(asignadoPor)}</span>
                        </div>
                      )
                    }
                    return null
                  })()}

                  {/* Comentarios */}
                  {tarea.cantidad_comentarios > 0 && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <MessageSquare className="h-3 w-3" />
                      <span>{tarea.cantidad_comentarios}</span>
                    </div>
                  )}
                </div>
              </div>
            </HoverCardTrigger>
            {/* Solo mostrar preview cuando no está en modo selección */}
            {!selectable && (
              <HoverCardContent side="right" align="start" className="w-80">
                <TareaPreviewContent tarea={tarea} />
              </HoverCardContent>
            )}
          </HoverCard>
        </div>
      </CardContent>
    </Card>
  )
}

interface TareaGrupoCardProps {
  grupo: GrupoDeTablero
  onVerGrupo?: (grupo: GrupoDeTablero) => void
}

/**
 * Tarjeta de un grupo de tareas, una por cliente, con el avance. El avance cuenta las tareas que el
 * usuario ve. Quien no es admin puede no ver todas, y la tarjeta se lo aclara.
 */
export function TareaGrupoCard({ grupo, onVerGrupo }: TareaGrupoCardProps) {
  const { esAdmin } = usePermissions()
  const resolveNombre = useUserNameResolver()
  const { visibles } = grupo
  const primera = [...visibles].sort((a, b) => (a.created_at ?? '').localeCompare(b.created_at ?? ''))[0]
  const terminadas = visibles.filter((t) => t.estado === 'completada').length
  const asignados = [...new Set(visibles.flatMap((t) => (t.asignado_a ? [t.asignado_a] : [])))]
  const fechas = [...new Set(visibles.map((t) => t.fecha_limite))]

  return (
    <Card className="transition-shadow hover:shadow-md">
      <CardContent className="p-0">
        <button
          type="button"
          onClick={() => onVerGrupo?.(grupo)}
          className="w-full text-left p-3 rounded-[inherit] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Badge variant="outline" className="text-xs gap-1 mb-1">
            <Users className="h-3 w-3" />
            Grupo
          </Badge>
          <h4 className="font-medium text-sm line-clamp-2 mb-2">{primera?.titulo}</h4>
          <div className="space-y-1.5">
            <p className="text-xs text-muted-foreground">
              {terminadas} de {visibles.length} clientes{esAdmin ? '' : ', de los que ves'}
            </p>
            <div className="h-1.5 rounded-full bg-muted overflow-hidden" aria-hidden="true">
              <div
                className="h-full bg-primary"
                style={{ width: `${visibles.length ? (terminadas / visibles.length) * 100 : 0}%` }}
              />
            </div>
            {asignados.length > 0 && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <User className="h-3 w-3" />
                <span className="truncate">
                  {asignados.length === 1 ? resolveNombre(asignados[0]) : `${asignados.length} responsables`}
                </span>
              </div>
            )}
            {(fechas.length > 1 || fechas[0]) && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Calendar className="h-3 w-3" />
                <span>
                  {fechas.length > 1
                    ? 'Varias fechas'
                    : format(parseISO(fechas[0]!), "d 'de' MMM", { locale: es })}
                </span>
              </div>
            )}
          </div>
        </button>
      </CardContent>
    </Card>
  )
}

// Versión simplificada para el overlay de drag
export function TareaCardOverlay({ tarea }: { tarea: TareaCompleta }) {
  return (
    <Card className="shadow-xl rotate-3 opacity-90">
      <CardContent className="p-3">
        <div className="flex items-start gap-2">
          <GripVertical className="h-4 w-4 mt-1 text-muted-foreground" />
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-1 mb-1">
              {tarea.prioridad === 'urgente' && (
                <Badge variant="outline" className="text-xs bg-red-100 text-red-800 border-red-200">Urgente</Badge>
              )}
              {tarea.tipo === 'inscripcion' && <InscripcionBadge />}
              <Badge variant="outline" className={cn('text-xs', ESTADO_STYLES[tarea.estado as string])}>
                {ESTADO_LABELS[tarea.estado as string] ?? tarea.estado}
              </Badge>
            </div>
            <h4 className="font-medium text-sm line-clamp-2">{tarea.titulo}</h4>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
